import { c as _c } from "react/compiler-runtime";
import { MinusCircle, Pencil, PlusCircle } from "lucide-react";
import { NodeKey } from "lexical";
import { ContentSummary, GridSlot, GridSlotSize } from "common/types/graphql";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import React, { PropsWithChildren, useCallback } from "react";
import { ImmutableGridSlotProps, ImmutableGridSlot } from "./ImmutableGridSlot";
import { Bugsnag } from "common/lib/bugsnag";
import { INDEX_FOR_NO_GRID_SLOT } from "modules/editor/blocks/Grid/Grid";
export interface MutableGridSlotProps extends Omit<ImmutableGridSlotProps, "isOver"> {
  /** The index of the grid slot. */
  index: number;
  gridNodeKey?: NodeKey;

  /**
   * A handler for the clear action.
   *
   * @param index The index of the grid slot.
   */
  onClear?: (index: number) => void;

  /**
   * A handler for the drop action.
   *
   * @param index The index of the grid slot.
   * @param item The dropped item.
   */
  onDrop?: (index: number, item: DragItem) => void;

  /**
   * A handler for swapping slots.
   *
   * @param a The index of the first slot.
   * @param b The index of the second slot.
   */
  onSwap?: (a: number, b: number, item: DragItem) => void;

  /**
   * A handler for the edit action.
   *
   * @param index The index of the grid slot.
   */
  onEdit?: (index: number) => void;
  /**
   * A handler for the add action.
   *
   * @param index The index of the grid slot.
   */
  onAdd?: (index: number) => void;

  /**
   * Handler for when something is being dragged over this
   * @param index The index of the grid slot
   */
  onDraggingOver?: (index: number) => void;

  /**
   * Boolean for whether something is currently dragged over this
   */
  dragOver?: boolean;
}
interface DragItem extends Partial<ContentSummary> {
  index: number;
  swappable: boolean;
  sourceGridNodeKey?: NodeKey;
  itemType?: GridSlot;
}

/** A grid slot that can be modified by a user */
export const MutableGridSlot = t0 => {
  "use memo";

  const $ = _c(49);
  const {
    index,
    gridNodeKey,
    size: t1,
    type: t2,
    asPlaceholder: t3,
    onClear,
    onDrop,
    onSwap,
    onEdit,
    onAdd,
    children,
    onDraggingOver,
    dragOver: t4
  } = t0;
  const size = t1 === undefined ? GridSlotSize.Base : t1;
  const type = t2 === undefined ? GridSlot.Editorial : t2;
  const asPlaceholder = t3 === undefined ? false : t3;
  const dragOver = t4 === undefined ? false : t4;
  const t = useTranslations("blocks.grid");
  let t5;
  if ($[0] !== index || $[1] !== onClear) {
    t5 = () => {
      onClear && onClear(index);
    };
    $[0] = index;
    $[1] = onClear;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const onClearButtonClick = t5;
  let t6;
  if ($[3] !== index || $[4] !== onEdit) {
    t6 = () => {
      onEdit && onEdit(index);
    };
    $[3] = index;
    $[4] = onEdit;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const onEditButtonClick = t6;
  let t7;
  if ($[6] !== index || $[7] !== onAdd) {
    t7 = () => {
      onAdd && onAdd(index);
    };
    $[6] = index;
    $[7] = onAdd;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const onAddButtonClick = t7;
  let t8;
  if ($[9] !== index || $[10] !== onDraggingOver) {
    t8 = ev => {
      ev.preventDefault();
      onDraggingOver && onDraggingOver(index);
    };
    $[9] = index;
    $[10] = onDraggingOver;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const dragOverHandler = t8;
  let t9;
  if ($[12] !== gridNodeKey || $[13] !== index || $[14] !== onDraggingOver || $[15] !== onDrop || $[16] !== onSwap || $[17] !== type) {
    t9 = ev_0 => {
      ev_0.preventDefault();
      let item;
      const dataTransfer = ev_0.dataTransfer;
      const swapData = dataTransfer.getData("gridSlot");
      const addData = dataTransfer.getData("contentSummary");
      const data = swapData || addData;
      onDraggingOver?.(INDEX_FOR_NO_GRID_SLOT);
      if (!data) {
        console.error("Drop handler for MutableGridSlot has no data");
        return;
      }
      ;
      try {
        item = JSON.parse(data).item;
      } catch (t10) {
        const e = t10;
        Bugsnag.notify(e as Error);
        console.error("Failed to parse the drop data", e);
        return;
      }
      if (item && item.sourceGridNodeKey === gridNodeKey && item.index !== index && item.itemType === type) {
        onSwap && onSwap(item.index, index, item);
      }
      if (item && item.sourceGridNodeKey !== gridNodeKey && item.itemType === type) {
        onDrop && onDrop(index, item);
      }
    };
    $[12] = gridNodeKey;
    $[13] = index;
    $[14] = onDraggingOver;
    $[15] = onDrop;
    $[16] = onSwap;
    $[17] = type;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const dropHandler = t9;
  let t10;
  if ($[19] !== gridNodeKey || $[20] !== index || $[21] !== type) {
    t10 = ev_1 => {
      ev_1.dataTransfer.clearData("gridSlot");
      ev_1.dataTransfer.setData("gridSlot", JSON.stringify({
        item: {
          index,
          sourceGridNodeKey: gridNodeKey,
          swappable: true,
          itemType: type
        }
      }));
    };
    $[19] = gridNodeKey;
    $[20] = index;
    $[21] = type;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const dragStartHandler = t10;
  let t11;
  if ($[23] !== onEdit || $[24] !== onEditButtonClick || $[25] !== t) {
    t11 = onEdit && <button onClick={onEditButtonClick} aria-label={t("customise_edit")} className="border border-r-0 border-gray-300 bg-white p-2">{t("edit")} <Pencil className="inline-block size-4" /></button>;
    $[23] = onEdit;
    $[24] = onEditButtonClick;
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== onAdd || $[28] !== onAddButtonClick || $[29] !== t) {
    t12 = onAdd && <button onClick={onAddButtonClick} aria-label={t("add_custom_content")} className="border border-r-0 border-gray-300 bg-white p-2">{t("add_custom_content")} <PlusCircle className="inline-block size-6" /></button>;
    $[27] = onAdd;
    $[28] = onAddButtonClick;
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== onClear || $[32] !== onClearButtonClick || $[33] !== t) {
    t13 = onClear && <button onClick={onClearButtonClick} aria-label={t("clear_slot")} className="border border-gray-300 bg-white p-2"><MinusCircle className="size-6 text-red-500" /></button>;
    $[31] = onClear;
    $[32] = onClearButtonClick;
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t14 = <div className="absolute right-0 hidden border-collapse shadow-md group-hover:flex">{t11}{t12}{t13}</div>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== asPlaceholder || $[40] !== children || $[41] !== dragOver || $[42] !== dragOverHandler || $[43] !== dragStartHandler || $[44] !== dropHandler || $[45] !== size || $[46] !== t14 || $[47] !== type) {
    t15 = <ImmutableGridSlot size={size} type={type} asPlaceholder={asPlaceholder} isOver={dragOver} handleDrop={dropHandler} handleDragOver={dragOverHandler} handleDragStart={dragStartHandler}>{children}{t14}</ImmutableGridSlot>;
    $[39] = asPlaceholder;
    $[40] = children;
    $[41] = dragOver;
    $[42] = dragOverHandler;
    $[43] = dragStartHandler;
    $[44] = dropHandler;
    $[45] = size;
    $[46] = t14;
    $[47] = type;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
};